import { el } from './ui'
let active: { root: HTMLElement; list: HTMLElement; close: () => void } | null = null
let serial = 0
// Capture outside taps before a sheet or step stops pointer bubbling for its swipe.
if (typeof document !== 'undefined') document.addEventListener('pointerdown', event => {
  if (active && !active.root.contains(event.target as Node) && !active.list.contains(event.target as Node)) active.close()
}, true)

// Keep the existing select and change handler as the source of truth. The
// visible list is an accessible single-choice control in Roundly's own style.
export function styleSelect(select: HTMLSelectElement): void {
  if (select.dataset.styled || select.hidden) return
  select.dataset.styled = 'true'
  const label = select.getAttribute('aria-label') ?? 'Choose an option'
  const root = el('div', { class: 'roundly-select' })
  const trigger = el('button', { type: 'button', class:'roundly-select-trigger', 'aria-label':label, 'aria-haspopup':'listbox', 'aria-expanded':'false' })
  const value = el('span', {class:'roundly-select-value'})
  const arrow = el('span', {class:'roundly-select-arrow','aria-hidden':'true'})
  trigger.append(value,arrow)
  const list = el('div', { class:'roundly-select-list', role:'listbox', id:`roundly-select-${++serial}`, 'aria-label':label })
  trigger.setAttribute('aria-controls',list.id)
  list.hidden = true
  const choices = [...select.querySelectorAll('option')]
  const buttons: HTMLButtonElement[] = []
  let mounted: MutationObserver | null = null
  const scrolled = (event: Event): void => { if (!list.contains(event.target as Node)) close() }
  const close = (): void => {
    list.hidden = true; trigger.setAttribute('aria-expanded','false')
    root.append(list)
    document.removeEventListener('scroll',scrolled,true)
    window.removeEventListener('resize',close)
    mounted?.disconnect(); mounted = null
    if (active?.root === root) active = null
  }
  const sync = (): void => {
    value.textContent = choices.find(o => o.value === select.value)?.textContent ?? choices[0]?.textContent ?? 'Choose'
    trigger.setAttribute('aria-disabled',String(select.disabled))
    buttons.forEach((b,i) => { b.setAttribute('aria-selected',String(choices[i]?.value === select.value)); b.disabled = !!choices[i]?.disabled })
  }
  const focusAt = (i: number): void => buttons[(i + buttons.length) % buttons.length]?.focus()
  const open = (): void => {
    if (select.disabled) return
    active?.close(); sync()
    // Render outside the grid and sticky card so opening never shifts or clips them.
    const rect = trigger.getBoundingClientRect()
    const viewport = window.visualViewport
    const leftEdge = (viewport?.offsetLeft ?? 0) + 8, topEdge = (viewport?.offsetTop ?? 0) + 8
    const rightEdge = leftEdge + (viewport?.width ?? window.innerWidth) - 16
    const bottomEdge = topEdge + (viewport?.height ?? window.innerHeight) - 16
    const width = Math.min(Math.max(rect.width,180),rightEdge-leftEdge)
    const below = bottomEdge-rect.bottom-6, above = rect.top-topEdge-6
    const upwards = below < 180 && above > below
    list.dataset.upwards = String(upwards)
    list.style.width = `${width}px`
    list.style.left = `${Math.max(leftEdge,Math.min(rect.left,rightEdge-width))}px`
    list.style.maxHeight = `${Math.max(44,Math.min(260,upwards ? above : below))}px`
    document.body.append(list); list.hidden = false
    list.style.top = `${upwards ? Math.max(topEdge,rect.top-6-list.getBoundingClientRect().height) : rect.bottom+6}px`
    trigger.setAttribute('aria-expanded','true'); active={root,list,close}
    document.addEventListener('scroll',scrolled,true)
    window.addEventListener('resize',close)
    if (typeof MutationObserver !== 'undefined') {
      mounted = new MutationObserver(() => { if (!root.isConnected) close() })
      mounted.observe(document.body,{childList:true,subtree:true})
    }
    focusAt(Math.max(0,choices.findIndex(o => o.value === select.value)))
  }
  trigger.addEventListener('click', () => { if (list.hidden) open(); else close() })
  trigger.addEventListener('keydown', event => { if (['ArrowDown','ArrowUp'].includes(event.key)) {event.preventDefault();open()} })
  let typed = '', typedAt = 0
  choices.forEach((option,i) => {
    const button = el('button', { type:'button', role:'option', tabindex:'-1', 'aria-selected':String(option.value === select.value) }, option.textContent ?? '', el('span', {class:'roundly-select-check','aria-hidden':'true'}, ' ✓'))
    button.addEventListener('click', () => {
      if (select.disabled || option.disabled) return
      select.value=option.value; close(); sync(); trigger.focus()
      select.dispatchEvent(new Event('change',{bubbles:true}))
    })
    button.addEventListener('keydown', event => {
      if (event.key === 'Escape') {event.preventDefault();close();trigger.focus()}
      else if (event.key === 'Tab') {trigger.focus();close()}
      else if (['ArrowDown','ArrowUp','Home','End'].includes(event.key)) {event.preventDefault();focusAt(event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length-1 : i+(event.key === 'ArrowDown' ? 1 : -1))}
      else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && event.key !== ' ') {
        typed = Date.now()-typedAt > 700 ? event.key : typed+event.key; typedAt=Date.now()
        const match=choices.findIndex(o => o.textContent?.toLowerCase().startsWith(typed.toLowerCase())); if(match>=0)focusAt(match)
      }
    })
    buttons.push(button);list.append(button)
  })
  select.parentElement?.insertBefore(root,select);root.append(trigger,list,select)
  select.hidden=true;select.setAttribute('aria-hidden','true');select.tabIndex=-1
  select.addEventListener('change',sync)
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(() => {sync();if(!root.isConnected)observer.disconnect()})
    observer.observe(select,{attributes:true,attributeFilter:['disabled']})
  }
  sync()
}
